import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Clock, MapPin, Pencil, Plus, Store, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { NotFound } from '../../../../pages/NotFound/NotFound';
import { selectHasPickupPoints } from '../../../../features/auth/authSelectors';
import {
  deletePickupPoint,
  fetchPickupPoints,
} from '../../../../features/pickupPoints/pickupPointsThunks';
import { scheduleLabel } from '../../../../features/pickupPoints/pickupPointsUtils';
import { getFullAddress } from '../../../../features/orderDetails/orderUtils';
import { PickupPointFormModal } from './PickupPointFormModal';

const PAGE_SIZE = 20;

/**
 * `/pickup-points`: "Mis puntos de retiro". The shops where the operator's
 * couriers leave parcels for the end customer to collect.
 *
 * Without the addon the address is the "page does not exist" screen, the same
 * way the sidebar leaves its entry out: hiding only the link would leave the
 * section reachable by typing it.
 */
export const PickupPoints = () => {
  const dispatch = useDispatch();
  const hasAccess = useSelector(selectHasPickupPoints);
  const { items, loading, loaded, error, deletingId } = useSelector((state) => state.pickupPoints);

  const [page, setPage] = useState(1);
  // `null` closed, `'new'` creating, a point editing.
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (hasAccess) dispatch(fetchPickupPoints());
  }, [dispatch, hasAccess]);

  if (!hasAccess) return <NotFound />;

  const handleDelete = async () => {
    setDeleteError(null);
    const action = await dispatch(deletePickupPoint(toDelete.id));
    if (action.meta.requestStatus === 'fulfilled') setToDelete(null);
    else setDeleteError(action.payload);
  };

  const columns = [
    {
      header: 'PUNTO DE RETIRO',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-bold text-ninja-dark dark:text-white">{row.name}</span>
          {(row.storekeeper_name || row.storekeeper_phone) && (
            <span className="text-xs text-text-muted mt-0.5">
              {[row.storekeeper_name, row.storekeeper_phone].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'DIRECCIÓN',
      cell: (row) => (
        <div className="flex items-start gap-1.5 text-text-secondary whitespace-normal max-w-md">
          <MapPin size={14} className="shrink-0 mt-0.5" />
          <span>
            {getFullAddress(row.address)}
            {row.warehouse_address_comments && (
              <span className="block text-xs text-text-muted">{row.warehouse_address_comments}</span>
            )}
          </span>
        </div>
      ),
    },
    {
      header: 'RECEPCIÓN',
      cell: (row) => (
        <span className="flex items-center gap-1.5 text-text-secondary">
          <Clock size={14} /> {scheduleLabel(row)}
        </span>
      ),
    },
    {
      header: '',
      cell: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="subtle"
            pill
            onClick={() => setEditing(row)}
            aria-label={`Editar ${row.name}`}
          >
            <Pencil size={14} /> Editar
          </Button>
          <Button
            variant="subtle"
            pill
            onClick={() => { setDeleteError(null); setToDelete(row); }}
            disabled={deletingId === row.id}
            className="text-danger"
            aria-label={`Eliminar ${row.name}`}
          >
            <Trash2 size={14} /> Eliminar
          </Button>
        </div>
      ),
    },
  ];

  const pageRows = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Store}
        title="Mis puntos de retiro"
        description="Los locales donde tus couriers dejan los pedidos para que el cliente final los retire."
        actions={
          <Button variant="primary" pill onClick={() => setEditing('new')}>
            <Plus size={16} /> Nuevo punto de retiro
          </Button>
        }
        testId="pickup-points-header"
      />

      <DataTable
        columns={columns}
        data={pageRows}
        totalItems={items.length}
        currentPage={page}
        itemsPerPage={PAGE_SIZE}
        onPageChange={setPage}
        isLoading={loading && !loaded}
        error={error}
        onRetry={() => dispatch(fetchPickupPoints())}
        itemLabel="puntos de retiro"
        emptyMessage="Todavía no tienes puntos de retiro. Crea el primero para poder redirigir envíos."
      />

      <PickupPointFormModal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        point={editing === 'new' ? null : editing}
      />

      <ConfirmDialog
        isOpen={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Eliminar punto de retiro"
        message={
          toDelete ? (
            <>
              <b>{toDelete.name}</b> dejará de aparecer como destino. Los envíos que ya se
              entregaron ahí conservan su historial.
            </>
          ) : null
        }
        consequences={['No se puede eliminar mientras tenga envíos en curso.']}
        confirmLabel="Eliminar"
        loadingLabel="Eliminando..."
        loading={Boolean(toDelete) && deletingId === toDelete.id}
        error={deleteError}
        testId="pickup-point-delete"
      />
    </div>
  );
};
